import { apiClient, createAuthHeaders } from '../api-client';

export interface UsageSummary {
  workspaceId: string;
  period: {
    start: string;
    end: string;
  };
  supabase: {
    edgeFunctionInvocations: number;
    realtimeMessages?: number;
    realtimePeakConnections?: number;
  };
  cloudflare?: {
    gatewayRequests: number;
    contentDeliveryRequests: number;
    r2AssetsStorageBytes: number;
  };
  byProject: {
    projectId: string;
    projectName: string;
    isDeleted?: boolean;
    edgeFunctionInvocations: number;
    gatewayRequests: number;
    contentDeliveryRequests: number;
    realtimeMessages?: number;
    realtimePeakConnections?: number;
  }[];
}

export interface DailyUsage {
  date: string;
  edgeFunctionInvocations: number;
}

export interface CloudBalanceDetails {
  planBalance: number;
  topupBalance: number;
  totalBalance: number;
  allocated: number;
  currentCycleUsed: number;
  tiersPurchased: Record<string, number>;
  lastResetAt?: string;
  nextResetAt?: string;
  billingSource: 'standalone' | 'embedded';
  screenCount: number;
  autoReplenish: boolean;
  replenishThreshold: number;
  replenishAmount: number;
}

export interface MeterCost {
  meter: string;
  usage: number;
  tiersPurchased: number;
  cost: number;
  included?: number;  // For instance meter - included projects per plan
}

export interface CloudBalanceWithUsage {
  balance: CloudBalanceDetails;
  usage: UsageSummary;
  costs: MeterCost[];
  totalCost: number;
}

export const cloudUsageApi = {
  /**
   * Get usage summary for the current month
   */
  async getSummary(token: string): Promise<UsageSummary> {
    const response = await apiClient.get('/api/cloud-usage/summary', {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Get daily usage breakdown
   */
  async getDaily(token: string, days: number = 30): Promise<DailyUsage[]> {
    const response = await apiClient.get('/api/cloud-usage/daily', {
      headers: createAuthHeaders(token),
      params: { days },
    });
    return response.data;
  },

  /**
   * Manually trigger usage sync
   */
  async sync(token: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post('/api/cloud-usage/sync', null, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Get cloud balance details
   */
  async getBalance(token: string): Promise<CloudBalanceDetails> {
    const response = await apiClient.get('/api/cloud-usage/balance', {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Get cloud balance with usage and costs
   */
  async getBalanceWithUsage(token: string): Promise<CloudBalanceWithUsage> {
    const response = await apiClient.get('/api/cloud-usage/balance-with-usage', {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Update auto-replenish settings
   */
  async updateAutoReplenish(
    token: string,
    settings: { enabled: boolean; threshold?: number; amount?: number }
  ): Promise<{ success: boolean }> {
    const response = await apiClient.patch('/api/cloud-usage/auto-replenish', settings, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },
};

/**
 * Format bytes to human readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Format number with commas
 */
export function formatNumber(num: number): string {
  return num.toLocaleString();
}
